import * as React from 'react'
import { useLoaderData } from 'react-router-dom';
import { Typography, Box, Button, TextField, Stack, FormControl, Box as MuiBox } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { defaultApi, usersApi } from '../../api-interface';
import { TenantContext } from '../..';
import { enqueueSnackbar } from 'notistack';


export async function loader(tenant_id) {
  const user = await defaultApi.readUsersMeMeGet()

  return {
    firstName: user.data.first_name,
    lastName: user.data.last_name
  };
}

// Validation schemas for each form
const firstNameSchema = yup.object({
  firstName: yup.string().required('First Name is required').min(2, 'First name must be at least two characters'),
});

const lastNameSchema = yup.object({
  lastName: yup.string().required('Last Name is required').min(2, 'Last name must be at least two characters'),
});

const passwordYup = yup.string()
  .required('Password is required')
  .min(8, 'Must contain at least 8 characters')
  .matches(/^(?=.*[a-z])/, 'Must contain one lowercase character')
  .matches(/^(?=.*[A-Z])/, 'Must contain one uppercase character')
  .matches(/^(?=.*[0-9])/, 'Must contain one number character')
  .matches(/^(?=.*[!@#\$\-%\^&\*_])/, 'Must contain one special character');

const passwordSchema = yup.object({
  currentPassword: yup.string().required('Current password is required'),
  newPassword: passwordYup,
  confirmNewPassword: yup.string()
    .required('Please confirm your new password')
    .oneOf([yup.ref('newPassword')], 'Passwords must match'),
});

const Profile = () => {
  const loaded_data = useLoaderData();
  const [editField, setEditField] = React.useState(null); // 'firstName', 'lastName', 'password', or null
  const tenant_id = React.useContext(TenantContext);

  // First Name Form
  const firstNameForm = useForm({
    resolver: yupResolver(firstNameSchema),
    defaultValues: {
      firstName: loaded_data.firstName || '',
    }
  });

  // Last Name Form
  const lastNameForm = useForm({
    resolver: yupResolver(lastNameSchema),
    defaultValues: {
      lastName: loaded_data.lastName || '',
    }
  });

  // Password Form
  const passwordForm = useForm({
    resolver: yupResolver(passwordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: ''
    }
  });

  React.useEffect(() => {
    firstNameForm.reset({ firstName: loaded_data.firstName || '' });
    lastNameForm.reset({ lastName: loaded_data.lastName || '' });
  }, [loaded_data, firstNameForm.reset, lastNameForm.reset]);

  const handleEdit = (field) => setEditField(field);
  
  const handleCancel = () => {
    // Reset the appropriate form based on what was being edited
    if (editField === 'firstName') {
      firstNameForm.reset({ firstName: loaded_data.firstName || '' });
    } else if (editField === 'lastName') {
      lastNameForm.reset({ lastName: loaded_data.lastName || '' });
    } else if (editField === 'password') {
      passwordForm.reset({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      });
    }
    setEditField(null);
  };

  const onFirstNameSubmit = async (data) => {
    try {
      console.log("Updating first name:", data);
      await usersApi(tenant_id).updateUserDetailsUsersDetailsPatch({
        first_name: data.firstName,
        last_name: undefined, 
        new_password: undefined,
        old_password: undefined
      });
      setEditField(null);
      enqueueSnackbar("First name updated successfully", { variant: "success" });
    } catch (error) {
      console.error("Error updating first name:", error);
      enqueueSnackbar("Failed to update first name", { variant: "error" });
    }
  };

  const onLastNameSubmit = async (data) => {
    try {
      console.log("Updating last name:", data);
      await usersApi(tenant_id).updateUserDetailsUsersDetailsPatch({
        first_name: undefined, // Keep existing first name
        last_name: data.lastName,
        new_password: undefined,
        old_password: undefined
      });
      setEditField(null);
      enqueueSnackbar("Last name updated successfully", { variant: "success" });
    } catch (error) {
      console.error("Error updating last name:", error);
      enqueueSnackbar("Failed to update last name", { variant: "error" });
    }
  };

  const onPasswordSubmit = async (data) => {
    try {
      console.log("Updating password");
      await usersApi(tenant_id).updateUserDetailsUsersDetailsPatch({
        first_name: undefined, // Keep existing names
        last_name: undefined,
        old_password: data.currentPassword,
        new_password: data.newPassword,
      });
      setEditField(null);
      passwordForm.reset({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      });
      enqueueSnackbar("Password updated successfully", { variant: "success" });
    } catch (error) {
      console.error("Error updating password:", error);
      if (error?.response?.data?.detail) {
        enqueueSnackbar(error.response.data.detail, { variant: "error" });
      } else {
        enqueueSnackbar("Failed to update password", { variant: "error" });
      }
    }
  };

  const onError = (errors) => {
    console.log("Form validation errors:", errors);
    enqueueSnackbar("Please fix the validation errors", { variant: "error" });
  };

  return (
    <Box>
      <Typography variant="h4">Profile</Typography>
      <Box mt={4} maxWidth={600}>
        <Typography variant="h6" gutterBottom>
          Personal Information
        </Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          {/* First Name */}
          <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
              <Typography variant="subtitle1" fontWeight={"bold"}>
                First Name:
              </Typography>
              {editField === 'firstName' ? (
                <FormControl>
                  <TextField
                    {...firstNameForm.register('firstName')}
                    label="First Name"
                    size="small"
                    error={!!firstNameForm.formState.errors.firstName}
                    helperText={firstNameForm.formState.errors.firstName?.message as string || ''}
                    variant="outlined"
                  />
                </FormControl>
              ) : (
                <Typography variant="body1">
                  {firstNameForm.watch('firstName') || 'N/A'}
                </Typography>
              )}
            </Box>
            {editField === 'firstName' ? (
              <Stack direction="row" spacing={1}>
                <Button 
                  variant='contained' 
                  onClick={firstNameForm.handleSubmit(onFirstNameSubmit, onError)}
                  disabled={firstNameForm.formState.isSubmitting}
                >
                  {firstNameForm.formState.isSubmitting ? 'Saving...' : 'Save'}
                </Button>
                <Button variant='text' onClick={handleCancel} disabled={firstNameForm.formState.isSubmitting}>Cancel</Button>
              </Stack>
            ) : (
              <Button variant='outlined' onClick={() => handleEdit('firstName')}>Edit</Button>
            )}
          </Box>
          {/* Last Name */}
          <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
              <Typography variant="subtitle1" fontWeight={"bold"}>
                Last Name:
              </Typography>
              {editField === 'lastName' ? (
                <FormControl>
                  <TextField
                    {...lastNameForm.register('lastName')}
                    label="Last Name"
                    size="small"
                    error={!!lastNameForm.formState.errors.lastName}
                    helperText={lastNameForm.formState.errors.lastName?.message as string || ''}
                    variant="outlined"
                  />
                </FormControl>
              ) : (
                <Typography variant="body1">
                  {lastNameForm.watch('lastName') || 'N/A'}
                </Typography>
              )}
            </Box>
            {editField === 'lastName' ? (
              <Stack direction="row" spacing={1}>
                <Button 
                  variant='contained' 
                  onClick={lastNameForm.handleSubmit(onLastNameSubmit, onError)}
                  disabled={lastNameForm.formState.isSubmitting}
                >
                  {lastNameForm.formState.isSubmitting ? 'Saving...' : 'Save'}
                </Button>
                <Button variant='text' onClick={handleCancel} disabled={lastNameForm.formState.isSubmitting}>Cancel</Button>
              </Stack>
            ) : (
              <Button variant='outlined' onClick={() => handleEdit('lastName')}>Edit</Button>
            )}
          </Box>
        </Box>
        <Typography variant="h6" gutterBottom mt={4}>
          Security
        </Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          {/* Password */}
          <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
              <Typography
                variant="subtitle1"
                fontWeight={"bold"}
                alignSelf='flex-start'
              >
                Password:
              </Typography>
              {editField === 'password' ? (
                <MuiBox component="form" display="flex" flexDirection="column" gap={1}>
                  <TextField
                    {...passwordForm.register('currentPassword')}
                    label="Current Password"
                    type="password"
                    size="small"
                    error={!!passwordForm.formState.errors.currentPassword}
                    helperText={passwordForm.formState.errors.currentPassword?.message || ''}
                    variant="outlined"
                  />
                  <TextField
                    {...passwordForm.register('newPassword')}
                    label="New Password"
                    type="password"
                    size="small"
                    error={!!passwordForm.formState.errors.newPassword}
                    helperText={passwordForm.formState.errors.newPassword?.message || ''}
                    variant="outlined"
                  />
                  <TextField
                    {...passwordForm.register('confirmNewPassword')}
                    label="Confirm New Password"
                    type="password"
                    size="small"
                    error={!!passwordForm.formState.errors.confirmNewPassword}
                    helperText={passwordForm.formState.errors.confirmNewPassword?.message || ''}
                    variant="outlined"
                  />
                </MuiBox>
              ) : (
                <Typography variant="body1">
                  ********
                </Typography>
              )}
            </Box>
            {editField === 'password' ? (
              <Stack direction="row" spacing={1} alignSelf='flex-start'>
                <Button 
                  variant='contained' 
                  onClick={passwordForm.handleSubmit(onPasswordSubmit, onError)}
                  disabled={passwordForm.formState.isSubmitting}
                >
                  {passwordForm.formState.isSubmitting ? 'Saving...' : 'Save'}
                </Button>
                <Button variant='text' onClick={handleCancel} disabled={passwordForm.formState.isSubmitting}>Cancel</Button>
              </Stack>
            ) : (
              <Button variant='outlined' onClick={() => handleEdit('password')}>Edit</Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default Profile